import { getConfig } from '../config/config';
import { getKeycloakInstance } from '../config/keycloak';
import {
  ChildDocument,
  CreateDocumentResponse,
  DocsPayload,
  DocumentsList,
  GroupDetails,
  UserDetails,
} from '../constants/types';
import { sortByProperty } from '../helpers/sorting-helper';
import { getTenant } from '../utils/getKeycloakConfig';
import makeRequest from './makeRequest';

const fetchGroups = async (): Promise<GroupDetails[]> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/groups`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return sortByProperty(data, 'name');
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchGroupById = async (groupId: string): Promise<GroupDetails> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/groups/${groupId}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const searchGroupByName = async (groupName: string): Promise<GroupDetails> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/groups?search=${groupName}&exact=true`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchUserGroups = async (userId = ''): Promise<GroupDetails[]> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}/groups`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return sortByProperty(data, 'name');
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchGroupsWithDescription = async (): Promise<GroupDetails[]> => {
  let documents: ChildDocument[] = [];
  const [data] = await Promise.all([fetchGroups()]);
  await fetchChildDocuments('/user-management/groups').then(
    async (res: DocumentsList) => {
      documents = [...res.data];
    },
  );
  const groups = sortByProperty(data, 'name');
  return groups.map(group => {
    const matchedGroup = documents.find(
      ({ name1 }: ChildDocument) => name1 === group.name,
    );

    return {
      ...group,
      description: matchedGroup?.otherString1 ?? '',
    };
  });
};

const fetchGroupMembers = async (groupId = ''): Promise<UserDetails[]> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/groups/${groupId}/members`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return sortByProperty(data, 'firstName', 'lastName');
  } catch (error: any) {
    throw error?.response;
  }
};

const createGroup = async (payload: GroupDetails): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/groups`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('POST', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const assignToGroups = async (
  userId: string,
  groupId: string,
): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}/groups/${groupId}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('PUT', url, '', headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const deleteGroup = async (id: string): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/groups/${id}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('DELETE', url, '', headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const removeFromGroups = async (
  userId: string,
  groupId: string,
): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}/groups/${groupId}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('DELETE', url, '', headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchChildDocuments = async (
  folderPath: string,
  searchByName?: string,
): Promise<DocumentsList> => {
  const url = `${
    getConfig().excelEngineApiEndpoint
  }/api/filemanager/SearchDocuments?folderPath=${folderPath}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };
  let payload: any = {};
  if (searchByName) {
    payload = {
      searchFields: [
        {
          fieldname: 'name1',
          operator: '=',
          fieldvalue: searchByName,
        },
      ],
    };
  }
  try {
    const { data } = await makeRequest('POST', url, payload, headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const createDocumentGroup = async (
  folderPath: string,
  payload: DocsPayload,
): Promise<CreateDocumentResponse> => {
  const url = `${
    getConfig().excelEngineApiEndpoint
  }/api/filemanager/CreateJsonDocument?folderPath=${folderPath}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };
  try {
    const { data } = await makeRequest('POST', url, payload, headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const updateDocumentGroup = async (
  id: string,
  payload: DocsPayload,
): Promise<void> => {
  const url = `${
    getConfig().excelEngineApiEndpoint
  }/api/filemanager/UpdateDocument/${id}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };
  try {
    await makeRequest('POST', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const deleteDocumentGroup = async (id: string): Promise<void> => {
  const url = `${
    getConfig().excelEngineApiEndpoint
  }/api/filemanager/DeleteDocument/${id}`;

  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('DELETE', url, '', headers);
  } catch (error: any) {
    throw error?.response;
  }
};

export {
  fetchGroups,
  fetchGroupById,
  searchGroupByName,
  fetchUserGroups,
  fetchGroupsWithDescription,
  createGroup,
  deleteGroup,
  assignToGroups,
  removeFromGroups,
  fetchChildDocuments,
  createDocumentGroup,
  updateDocumentGroup,
  deleteDocumentGroup,
  fetchGroupMembers,
};
